/*
 * @Copyright 2024-2025. Institute for Future Intelligence, Inc.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from '../stores/common.ts';
import * as Selector from '../stores/selector';
import { DraggableBounds, DraggableData, DraggableEvent } from 'react-draggable';
import { App, Button, Drawer, Dropdown, Input, Modal, Segmented, Space, Table, Typography } from 'antd';
import {
  CaretDownOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  QuestionCircleOutlined,
  UndoOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { HOME_URL, REGEX_ALLOWABLE_IN_NAME, TRASH_RETENTION_DAYS } from '../constants.ts';
import { setMessage } from '../helpers.tsx';
import Draggable from 'react-draggable';
import { usePrimitiveStore } from '../stores/commonPrimitive.ts';
import { useTranslation } from 'react-i18next';
import type { InputRef } from 'antd';
import { MenuProps } from 'antd/lib';
import { AntdMenuItem } from '../components/menuItem.tsx';
import { ProjectInfo } from '../types.ts';
import dayjs from 'dayjs';
import { Util } from '../Util.ts';
import i18n from '../i18n/i18n.ts';
import { useIsMobile } from '../hooks';

const { Column } = Table;

export interface ProjectListPanelProps {
  projects: object[];
  loadProject: (uid: string, title: string) => Promise<void>;
  deleteProject: (title: string) => Promise<void>;
  restoreProject: (title: string) => Promise<void>;
  deleteProjectForever: (title: string) => Promise<void>;
  emptyTrash: () => Promise<void>;
  fetchMyTrash: () => Promise<ProjectInfo[]>;
  renameProject: (oldTitle: string, newTitle: string) => void;
  publishProject: (title: string) => Promise<boolean>;
  unpublishProject: (title: string) => Promise<boolean>;
}

const ProjectListPanel = React.memo(
  ({
    projects,
    loadProject,
    deleteProject,
    restoreProject,
    deleteProjectForever,
    emptyTrash,
    fetchMyTrash,
    renameProject,
    publishProject,
    unpublishProject,
  }: ProjectListPanelProps) => {
    const language = useStore(Selector.language);
    const user = useStore(Selector.user);
    const setCommonStore = useStore(Selector.set);
    // Title of the project currently open, so we can mark its row in the list.
    const currentProjectTitle = useStore(Selector.projectTitle);

    const projectsWithKeys = useMemo(() => {
      const arr: object[] = [];
      for (const [i, f] of projects.entries()) {
        arr.push({ ...f, key: i });
      }
      return arr;
    }, [projects]);

    // The panel mounts only while showProjectListPanel is true. Start the
    // drawer closed and flip it open on mount so the slide-in animation plays;
    // on close, animate shut first and clear the store flag (which unmounts
    // us) in afterOpenChange once the animation has settled.
    const [open, setOpen] = useState(false);
    useEffect(() => {
      setOpen(true);
    }, []);

    const [renameDialogVisible, setRenameDialogVisible] = useState(false);
    const [dragEnabled, setDragEnabled] = useState<boolean>(false);
    const [bounds, setBounds] = useState<DraggableBounds>({ left: 0, top: 0, bottom: 0, right: 0 } as DraggableBounds);
    const [oldTitle, setOldTitle] = useState<string>();
    const [newTitle, setNewTitle] = useState<string>();
    const dragRef = useRef<HTMLDivElement | null>(null);
    // make an editable copy because the project array is not mutable
    const projectsRef = useRef<object[]>(projectsWithKeys);
    // set a flag so that we can update when projectsRef changes
    const [recountFlag, setRecountFlag] = useState<boolean>(false);
    const [selectedIndex, setSelectedIndex] = useState<number>(-1);
    const searchRef = useRef<InputRef>(null);

    // Toggle between the live "My Projects" list and the "Trash" (soft-deleted)
    // list. Trash is fetched lazily the first time the tab is opened, and
    // re-fetched after a restore / permanent delete so the count stays honest.
    const [viewMode, setViewMode] = useState<'projects' | 'trash'>('projects');
    const [trash, setTrash] = useState<ProjectInfo[]>([]);
    const [trashLoading, setTrashLoading] = useState(false);
    // Only show a Trash count on the tab once it's been fetched at least once —
    // before that we don't know it, and showing "(0)" would be misleading.
    const [trashLoaded, setTrashLoaded] = useState(false);

    const loadTrash = async () => {
      setTrashLoading(true);
      try {
        const list = await fetchMyTrash();
        setTrash(list);
        setTrashLoaded(true);
      } finally {
        setTrashLoading(false);
      }
    };

    useEffect(() => {
      if (viewMode === 'trash') loadTrash();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewMode]);

    const { Search } = Input;
    const { modal } = App.useApp();
    const { t } = useTranslation();
    const lang = useMemo(() => {
      return { lng: language };
    }, [language]);
    const isMobile = useIsMobile();

    useEffect(() => {
      if (projects) {
        projectsRef.current = projectsWithKeys;
        setRecountFlag(!recountFlag);
      }
    }, [projects]);

    const closePanel = () => {
      usePrimitiveStore.getState().set((state) => {
        state.showProjectListPanel = false;
      });
    };

    const confirmDeleteProject = (title: string) => {
      modal.confirm({
        title: t('projectListPanel.DoYouReallyWantToDeleteProject', lang) + ' "' + title + '"?',
        // Delete is now a soft delete (recoverable from Trash), so no scary
        // "cannot be undone" warning — just say where it goes.
        content: (
          <span style={{ color: 'gray' }}>
            {t('projectListPanel.TrashRetentionExplanation', lang).replace('{DAYS}', String(TRASH_RETENTION_DAYS))}
          </span>
        ),
        icon: <QuestionCircleOutlined />,
        onOk: async () => {
          await deleteProject(title);
          // Refresh the Trash list so its tab count reflects the new item
          // immediately, instead of only when the Trash tab is next opened.
          await loadTrash();
        },
      });
    };

    const handleRestore = async (title: string) => {
      // restoreProject owns the success / already-purged messaging.
      await restoreProject(title);
      setTrash((prev) => prev.filter((p) => p.title !== title));
    };

    const confirmDeleteForever = (title: string) => {
      modal.confirm({
        title: t('projectListPanel.DoYouReallyWantToDeleteProjectForever', lang) + ' "' + title + '"?',
        content: (
          <span style={{ color: 'red', fontWeight: 'bold' }}>
            <WarningOutlined style={{ marginRight: '6px' }} />
            {t('word.Warning', lang) + ': ' + t('message.ThisCannotBeUndone', lang)}
          </span>
        ),
        icon: <QuestionCircleOutlined />,
        onOk: async () => {
          await deleteProjectForever(title);
          setTrash((prev) => prev.filter((p) => p.title !== title));
        },
      });
    };

    const confirmEmptyTrash = () => {
      modal.confirm({
        title: t('projectListPanel.DoYouReallyWantToEmptyTrash', lang),
        content: (
          <span style={{ color: 'red', fontWeight: 'bold' }}>
            <WarningOutlined style={{ marginRight: '6px' }} />
            {t('word.Warning', lang) + ': ' + t('message.ThisCannotBeUndone', lang)}
          </span>
        ),
        icon: <QuestionCircleOutlined />,
        onOk: async () => {
          await emptyTrash();
          setTrash([]);
        },
      });
    };

    const changeProjectTitle = () => {
      if (oldTitle && newTitle) {
        renameProject(oldTitle, newTitle);
        setNewTitle(undefined);
        setRecountFlag(!recountFlag);
      }
      setRenameDialogVisible(false);
    };

    const onStart = (event: DraggableEvent, uiData: DraggableData) => {
      if (dragRef.current) {
        const { clientWidth, clientHeight } = window.document.documentElement;
        const targetRect = dragRef.current.getBoundingClientRect();
        setBounds({
          left: -targetRect.left + uiData.x,
          right: clientWidth - (targetRect.right - uiData.x),
          top: -targetRect.top + uiData.y,
          bottom: clientHeight - (targetRect?.bottom - uiData.y),
        });
      }
    };

    const openProject = (record: ProjectInfo) => {
      const uid = user.uid;
      if (!uid) return;
      usePrimitiveStore.getState().set((state) => {
        state.startSimulation = false;
      });
      if (usePrimitiveStore.getState().changed) {
        modal.confirm({
          title: t('message.DoYouWantToSaveChanges', lang),
          icon: <ExclamationCircleOutlined />,
          onOk: () => {
            if (user.uid) {
              setCommonStore((state) => {
                state.projectToOpen = record;
              });
              usePrimitiveStore.getState().set((state) => {
                state.saveAndThenOpenProjectFlag = true;
              });
            } else {
              setMessage('info', t('message.ToSaveYourWorkPleaseSignIn', lang));
            }
          },
          onCancel: () => {
            loadProject(uid, record.title);
          },
          okText: t('word.Yes', lang),
          cancelText: t('word.No', lang),
        });
      } else {
        loadProject(uid, record.title);
      }
    };

    return (
      <>
        <Modal
          title={
            <div
              style={{ width: '100%', cursor: 'move' }}
              onMouseOver={() => setDragEnabled(true)}
              onMouseOut={() => setDragEnabled(false)}
            >
              {t('word.Rename', lang)}
            </div>
          }
          open={renameDialogVisible}
          onOk={changeProjectTitle}
          onCancel={() => {
            setRenameDialogVisible(false);
            setNewTitle(undefined);
          }}
          modalRender={(modal) => (
            <Draggable
              nodeRef={dragRef}
              disabled={!dragEnabled}
              bounds={bounds}
              onStart={(event, uiData) => onStart(event, uiData)}
            >
              <div ref={dragRef}>{modal}</div>
            </Draggable>
          )}
        >
          <Space direction={'vertical'} style={{ width: '100%' }}>
            <Input
              placeholder="Title"
              value={newTitle ? newTitle : oldTitle}
              onPressEnter={changeProjectTitle}
              onKeyDown={(e) => {
                if (!REGEX_ALLOWABLE_IN_NAME.test(e.key)) {
                  e.preventDefault();
                  return false;
                }
                e.stopPropagation();
              }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setNewTitle(e.target.value);
              }}
            />
            <span style={{ fontSize: '11px', color: 'red' }}>
              <WarningOutlined style={{ marginRight: '4px' }} />
              {t('word.Caution', lang) +
                ': ' +
                t('projectListPanel.IfSharedOrPublishedRenamingProjectBreaksExistingLinks', lang)}
              .
            </span>
          </Space>
        </Modal>
        <Drawer
          // The tab toggle doubles as the header — the static title was
          // redundant with it. Counts ride on each tab.
          title={
            <Segmented
              size={'small'}
              value={viewMode}
              onChange={(v) => setViewMode(v as 'projects' | 'trash')}
              options={[
                {
                  label: t('projectListPanel.MyProjects', lang) + ' (' + projectsRef.current.length + ')',
                  value: 'projects',
                },
                {
                  label: (
                    <span>
                      <DeleteOutlined style={{ marginRight: 4 }} />
                      {t('projectListPanel.Trash', lang) + (trashLoaded ? ' (' + trash.length + ')' : '')}
                    </span>
                  ),
                  value: 'trash',
                },
              ]}
            />
          }
          placement="right"
          open={open}
          onClose={() => setOpen(false)}
          // No backdrop — the lab canvas stays live and interactive while the
          // user browses their projects. Close via the header icon or ESC.
          mask={false}
          // Cap at 90vw so the drawer doesn't dominate a narrow laptop screen.
          width={isMobile ? '92vw' : 'min(560px, 90vw)'}
          styles={{
            body: { padding: 0, display: 'flex', flexDirection: 'column' },
            header: { padding: '12px 16px' },
          }}
          destroyOnClose={false}
          // Bigger close icon on mobile — antd's default 16px hit area is hard
          // to land with a thumb.
          closeIcon={isMobile ? <span style={{ fontSize: 20, lineHeight: 1 }}>×</span> : undefined}
          // antd fires afterOpenChange once the open/close animation settles.
          // On open, focus the search box so the user can filter immediately;
          // on close, clear the store flag there so the slide-out finishes
          // before we unmount.
          afterOpenChange={(o) => {
            if (o) searchRef.current?.focus();
            else closePanel();
          }}
        >
          {viewMode === 'projects' && (
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
              <Search
                ref={searchRef}
                style={{ width: '100%' }}
                title={t('projectListPanel.SearchByTitle', lang)}
                placeholder={t('projectListPanel.SearchByTitle', lang)}
                allowClear
                size={'small'}
                enterButton
                onKeyDown={(e) => {
                  e.stopPropagation(); // do this to prevent this event from passing to the canvas behind it
                }}
                onSearch={(s) => {
                  if (!projects) return;
                  // must create a new array for ant table to update (don't just set length to 0)
                  projectsRef.current = [];
                  for (const [i, f] of projects.entries()) {
                    // @ts-expect-error: ignore
                    if (f['title']?.toLowerCase().includes(s.toLowerCase())) {
                      projectsRef.current.push({ ...f, key: i });
                    }
                  }
                  setRecountFlag(!recountFlag);
                }}
              />
            </div>
          )}
          {viewMode === 'projects' && (
            <div style={{ flex: 1, overflowY: 'auto', padding: '8px 16px' }}>
              <Table
                size={'small'}
                style={{ width: '100%', verticalAlign: 'top' }}
                dataSource={projectsRef.current}
                // Highlight the whole row on hover, not just the caret icon.
                onRow={(record, index) => ({
                  onMouseEnter: () => setSelectedIndex(index ?? -1),
                  onMouseLeave: () => setSelectedIndex(-1),
                })}
                pagination={{
                  defaultPageSize: 10,
                  showSizeChanger: true,
                  position: ['bottomCenter'],
                  pageSizeOptions: ['10', '20', '50'],
                }}
              >
                <Column
                  title={`${t('word.Title', lang)}`}
                  dataIndex="title"
                  key="title"
                  width={isMobile ? '100%' : '50%'}
                  sortDirections={['ascend', 'descend', 'ascend']}
                  sorter={(a: any, b: any) => {
                    return a['title'].localeCompare(b['title']);
                  }}
                  render={(title, record, index) => {
                    const isShared = (record as ProjectInfo).isPublic === true;
                    const shareMenuItems: MenuProps['items'] = isShared
                      ? [
                          {
                            key: 'remove-from-commons',
                            label: (
                              <AntdMenuItem
                                onClick={() => {
                                  modal.confirm({
                                    title:
                                      t('projectListPanel.DoYouReallyWantToRemoveProjectFromCommons', lang) +
                                      ' "' +
                                      title +
                                      '"?',
                                    icon: <QuestionCircleOutlined />,
                                    onOk: () => {
                                      unpublishProject(title).then((ok) => {
                                        if (ok)
                                          setMessage(
                                            'success',
                                            t('projectListPanel.ProjectRemovedFromCommons', lang) + '.',
                                          );
                                      });
                                    },
                                  });
                                }}
                              >
                                {t('projectListPanel.RemoveFromCommons', lang)}
                              </AntdMenuItem>
                            ),
                          },
                          {
                            key: 'copy-shared-link',
                            label: (
                              <AntdMenuItem
                                onClick={() => {
                                  // Deep-link directly to the shared project; the Commons
                                  // route lists everything, this one opens the project.
                                  if (user.uid) {
                                    const url =
                                      HOME_URL + '?userid=' + user.uid + '&project=' + encodeURIComponent(title);
                                    navigator.clipboard.writeText(url).then(() => {
                                      setMessage(
                                        'success',
                                        i18n.t('projectListPanel.SharedLinkCopiedToClipBoard', lang) + '.',
                                      );
                                    });
                                  }
                                }}
                              >
                                {t('projectListPanel.CopySharedLink', lang)}
                              </AntdMenuItem>
                            ),
                          },
                        ]
                      : [
                          {
                            key: 'share-to-commons',
                            label: (
                              <AntdMenuItem
                                onClick={() => {
                                  modal.confirm({
                                    title:
                                      t('projectListPanel.DoYouReallyWantToShareProject', lang) + ' "' + title + '"?',
                                    content: t('projectListPanel.ShareProjectExplanation', lang),
                                    icon: <QuestionCircleOutlined />,
                                    onOk: () => {
                                      publishProject(title).then((ok) => {
                                        if (ok) setMessage('success', t('projectListPanel.ProjectShared', lang) + '.');
                                      });
                                    },
                                  });
                                }}
                              >
                                {t('projectListPanel.ShareToCommons', lang)}
                              </AntdMenuItem>
                            ),
                          },
                        ];
                    const items: MenuProps['items'] = [
                      {
                        key: 'project-title',
                        label: (
                          <>
                            <AntdMenuItem fontWeight={'bold'}>
                              {title}
                              {isShared ? ' — ' + t('projectListPanel.InCommons', lang) : ''}
                            </AntdMenuItem>
                            <hr />
                          </>
                        ),
                      },
                      {
                        key: 'open-project',
                        label: (
                          <AntdMenuItem onClick={() => openProject(record as ProjectInfo)}>
                            {t('word.Open', lang)}
                          </AntdMenuItem>
                        ),
                      },
                      {
                        key: 'copy-title-to-clip-board',
                        label: (
                          <AntdMenuItem
                            onClick={() => {
                              navigator.clipboard.writeText(title).then(() => {
                                setMessage('success', i18n.t('projectListPanel.TitleCopiedToClipBoard', lang) + '.');
                              });
                            }}
                          >
                            {t('projectListPanel.CopyTitle', lang)}
                          </AntdMenuItem>
                        ),
                      },
                      {
                        key: 'rename-project',
                        label: (
                          <AntdMenuItem
                            onClick={() => {
                              setOldTitle(title);
                              setRenameDialogVisible(true);
                            }}
                          >
                            {t('word.Rename', lang)}
                          </AntdMenuItem>
                        ),
                      },
                      {
                        key: 'delete-project',
                        label: (
                          <AntdMenuItem onClick={() => confirmDeleteProject(title)}>
                            {t('word.Delete', lang)}
                          </AntdMenuItem>
                        ),
                      },
                      {
                        key: 'generate-project-link',
                        label: (
                          <AntdMenuItem
                            onClick={() => {
                              if (user.uid)
                                Util.generateProjectLink(user.uid, title, () => {
                                  setMessage(
                                    'success',
                                    i18n.t('projectListPanel.ProjectLinkGeneratedInClipBoard', lang) + '.',
                                  );
                                });
                            }}
                          >
                            {t('projectListPanel.GenerateProjectLink', lang)}
                          </AntdMenuItem>
                        ),
                      },
                      ...shareMenuItems,
                    ];

                    return (
                      <Space style={{ width: '100%' }}>
                        <Dropdown menu={{ items }} trigger={['click']}>
                          <CaretDownOutlined style={{ fontSize: '12px', cursor: 'pointer' }} />
                        </Dropdown>
                        <Typography.Text
                          style={{
                            fontSize: '12px',
                            cursor: 'pointer',
                            verticalAlign: 'top',
                            // Bold the project that's currently open so the user
                            // can see where they are in the list.
                            fontWeight: (record as ProjectInfo).title === currentProjectTitle ? 'bold' : 'normal',
                          }}
                          title={t('word.Open', lang)}
                          onClick={() => {
                            const selection = window.getSelection();
                            if (selection && selection.toString().length > 0) return;
                            // only proceed when no text is selected
                            openProject(record as ProjectInfo);
                          }}
                        >
                          {title}
                        </Typography.Text>
                      </Space>
                    );
                  }}
                  onCell={(data, index) => {
                    return {
                      style: {
                        background:
                          selectedIndex === index
                            ? 'lightskyblue'
                            : index !== undefined && index % 2 === 0
                              ? 'beige'
                              : 'gainsboro',
                      },
                    };
                  }}
                />
                {!isMobile && (
                  <Column
                    title={`${t('word.Time', lang)}`}
                    dataIndex="timestamp"
                    key="timestamp"
                    width={'25%'}
                    defaultSortOrder={'descend'}
                    sortDirections={['ascend', 'descend', 'ascend']}
                    sorter={(a: any, b: any) => {
                      return a['timestamp'] - b['timestamp'];
                    }}
                    render={(timestamp) => {
                      return (
                        <Typography.Text style={{ fontSize: '11px', verticalAlign: 'top' }}>
                          {dayjs(new Date(timestamp)).format('MM/DD/YYYY hh:mm A')}
                        </Typography.Text>
                      );
                    }}
                    onCell={(data, index) => {
                      return {
                        style: {
                          background:
                            selectedIndex === index
                              ? 'lightskyblue'
                              : index !== undefined && index % 2 === 0
                                ? 'beige'
                                : 'gainsboro',
                        },
                      };
                    }}
                  />
                )}
                {!isMobile && (
                  <Column
                    title={`${t('word.Type', lang)}`}
                    dataIndex="type"
                    key="type"
                    width={'25%'}
                    sortDirections={['ascend', 'descend', 'ascend']}
                    sorter={(a: any, b: any) => {
                      return a['type'].localeCompare(b['type']);
                    }}
                    render={(type) => {
                      return (
                        <Typography.Text style={{ fontSize: '11px', verticalAlign: 'top' }}>{type}</Typography.Text>
                      );
                    }}
                    onCell={(data, index) => {
                      return {
                        style: {
                          background:
                            selectedIndex === index
                              ? 'lightskyblue'
                              : index !== undefined && index % 2 === 0
                                ? 'beige'
                                : 'gainsboro',
                        },
                      };
                    }}
                  />
                )}
              </Table>
            </div>
          )}
          {viewMode === 'trash' && (
            <div style={{ flex: 1, overflowY: 'auto', padding: '8px 16px' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '8px',
                }}
              >
                <span style={{ fontSize: '11px', color: 'gray', flex: 1 }}>
                  {t('projectListPanel.TrashRetentionExplanation', lang).replace(
                    '{DAYS}',
                    String(TRASH_RETENTION_DAYS),
                  )}
                </span>
                <Button
                  size={'small'}
                  danger
                  icon={<DeleteOutlined />}
                  disabled={trash.length === 0}
                  onClick={confirmEmptyTrash}
                >
                  {t('projectListPanel.EmptyTrash', lang)}
                </Button>
              </div>
              <Table
                size={'small'}
                style={{ width: '100%', verticalAlign: 'top' }}
                dataSource={trash.map((p, i) => ({ ...p, key: i }))}
                loading={trashLoading}
                locale={{ emptyText: t('projectListPanel.TrashIsEmpty', lang) }}
                pagination={{ defaultPageSize: 10, position: ['bottomCenter'], pageSizeOptions: ['10', '20', '50'] }}
              >
                <Column
                  title={`${t('word.Title', lang)}`}
                  dataIndex="title"
                  key="title"
                  width={isMobile ? '45%' : '40%'}
                  render={(title) => (
                    <Typography.Text style={{ fontSize: '12px', verticalAlign: 'top' }}>{title}</Typography.Text>
                  )}
                />
                <Column
                  title={`${t('projectListPanel.PurgesIn', lang)}`}
                  dataIndex="deletedAt"
                  key="deletedAt"
                  width={isMobile ? '30%' : '35%'}
                  defaultSortOrder={'ascend'}
                  sorter={(a: any, b: any) => (a['deletedAt'] ?? 0) - (b['deletedAt'] ?? 0)}
                  render={(deletedAt) => {
                    // Whole days until the scheduled purge; clamp at 0 so an
                    // entry the cron hasn't swept yet reads "0 days", not negative.
                    const msLeft = (deletedAt ?? 0) + TRASH_RETENTION_DAYS * 86400000 - Date.now();
                    const daysLeft = Math.max(0, Math.ceil(msLeft / 86400000));
                    return (
                      <Typography.Text
                        style={{ fontSize: '11px', verticalAlign: 'top', color: daysLeft <= 3 ? 'red' : undefined }}
                        title={dayjs(new Date(deletedAt)).format('MM/DD/YYYY hh:mm A')}
                      >
                        {t('projectListPanel.NDays', lang).replace('{N}', String(daysLeft))}
                      </Typography.Text>
                    );
                  }}
                />
                <Column
                  title={`${t('word.Action', lang)}`}
                  key="action"
                  width={isMobile ? '25%' : '25%'}
                  render={(_, record) => {
                    const title = (record as ProjectInfo).title;
                    return (
                      <Space size={'small'}>
                        <Button
                          size={'small'}
                          type={'link'}
                          icon={<UndoOutlined />}
                          style={{ fontSize: '11px', padding: 0 }}
                          onClick={() => handleRestore(title)}
                        >
                          {t('word.Restore', lang)}
                        </Button>
                        <Button
                          size={'small'}
                          type={'link'}
                          danger
                          icon={<DeleteOutlined />}
                          style={{ fontSize: '11px', padding: 0 }}
                          onClick={() => confirmDeleteForever(title)}
                          title={t('projectListPanel.DeleteForever', lang)}
                        />
                      </Space>
                    );
                  }}
                />
              </Table>
            </div>
          )}
        </Drawer>
      </>
    );
  },
);

export default ProjectListPanel;
